import { effect, signal, WritableSignal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { DeepSignal } from '@ngrx/signals';
import { Observable, pipe, UnaryFunction } from 'rxjs';

export type StoreErrorState<T extends string> = {
  [key in T]: WritableSignal<Map<string, unknown>>;
};
export type StoreLoadingState<T extends string> = {
  [key in T]: WritableSignal<string[]>;
};
export type StoreSuccessState<T extends string> = {
  [key in T]: WritableSignal<Map<string, boolean>>;
};

const createStoreLoadingState = <T extends string>(options: T[]) => {
  const state: Record<string, WritableSignal<string[]>> = {};
  options.forEach((opt) => {
    state[opt] = signal([]);
  });
  return state as StoreLoadingState<T>;
};

const createStoreErrorState = <T extends string>(options: T[]) => {
  const state: Record<string, WritableSignal<Map<string, unknown>>> = {};
  options.forEach((opt) => {
    state[opt] = signal(new Map());
  });
  return state as StoreErrorState<T>;
};

const createStoreSuccessState = <T extends string>(options: T[]) => {
  const state: Record<string, WritableSignal<Map<string, boolean>>> = {};
  options.forEach((opt) => {
    state[opt] = signal(new Map());
  });
  return state as StoreSuccessState<T>;
};

/**
 * This function creates a default loading and error as well as a utility function
 * to wrap functions with
 * */
export const useStoreCreationUtils = <A extends string>(opts: {
  /**
   * Actions that need an error or loading state should be added here
   * */
  actions: A[];
}) => {
  const errors = createStoreErrorState(opts.actions);
  const loading = createStoreLoadingState(opts.actions);
  const success = createStoreSuccessState(opts.actions);

  /**
   * Executes a function as an action, keeping track of it's error state and loading state.
   * */
  function handleActionPipe<T, R>(
    action: A,
    id: string | undefined,
  ): UnaryFunction<Observable<T>, Observable<T>> {
    // Add the action to the loading state, and clear error state
    loading[action].update((state) => [...state, id ? id : action]);

    errors[action].update((state) => {
      state.delete(id ? id : action);
      return new Map(state);
    });

    success[action].update((state) => {
      state.delete(id ? id : action);
      return new Map(state);
    });

    return pipe(
      tapResponse({
        next: () => {
          success[action].update((state) => {
            state.set(id ? id : action, true);
            return new Map(state);
          });
        },
        // Set the error state if there is one
        error: (e: { message: string }) => {
          errors[action].update((state) => {
            state.set(id ? id : action, e);
            return new Map(state);
          });
          success[action].update((state) => {
            state.set(id ? id : action, false);
            return new Map(state);
          });
        },
        // Remove the action from the loading state when done
        finalize: () => {
          if (id) {
            const i = loading[action]().findIndex((i) => i === id);
            if (i !== -1)
              loading[action].update((state) => {
                state.splice(i, 1);
                return [...state];
              });
          } else {
            loading[action].update((state) => {
              state.pop();
              return [...state];
            });
          }
        },
      }),
    );
  }

  return {
    state: { errors, loading, success },
    handleActionPipe,
  };
};

export function listenToErrorState<A extends string>(
  storeProps: { errors: DeepSignal<StoreErrorState<A>> },
  actions: A[],
  hasErrorCallBack: (action: A) => void,
): void {
  actions.forEach((action) => {
    let oldErrorCount: number = 0;
    effect(() => {
      const newErrorCount = storeProps.errors()[action]().size;
      const previousErrorCount = oldErrorCount;
      oldErrorCount = storeProps.errors()[action]().size;

      if (newErrorCount === 0 || newErrorCount <= previousErrorCount) return;

      hasErrorCallBack(action);
    });
  });
}
